/**
 * Correct Score / Half Time Correct Score picker.
 * +/- score controls; odd below; tap to make a pick.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Trash2, Minus, Plus } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { getCorrectScorePickerStyles } from './CorrectScorePicker.styles';
import {
  getOddForScore,
  formatScoreDisplay,
  formatOddDisplay,
  getCorrectScoreBounds,
  getScoreKey,
  parseScoreKey,
  buildScoreLookup,
  getNeighborInfo,
  formatDeltaLabelFixed,
  getDeltaStyleKeyVisual,
  type ScorePickerValue,
} from './correctScorePicker.helper';
import { MARKET_BET_DESCRIPTIONS } from '../../../constants/tooltips';
import type { MarketOdds } from '../../../src/create/types/create.types';
import { PickInfoIcon } from './PickInfoIcon';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';

type CorrectScorePickerStyles = ReturnType<typeof getCorrectScorePickerStyles>;

export interface CorrectScorePickerProps {
  title: string;
  homeName: string;
  awayName: string;
  market: MarketOdds;
  gameId: string;
  handleAddOtherPick?: (
    gameId: string,
    marketId: number,
    marketName: string,
    value: string,
    odd: number,
    homeName: string,
    awayName: string
  ) => void;
  isOtherPickSelected?: (gameId: string, marketId: number, value: string) => boolean;
  /** When set (e.g. accept flow), picker initializes and syncs to this score so user only changes with +/-. */
  initialScoreValue?: string;
  /** When set, show a trash button to remove this pick (e.g. predicting step). */
  onDelete?: () => void;
  /** Optional testID for the delete button (e.g. for storybook play tests). */
  deleteButtonTestID?: string;
  /** When true, skip the outer sectionCard wrapper and title row (e.g. inline expanded card). */
  inline?: boolean;
}

interface ScorePickerColumnProps {
  score: ScorePickerValue;
  onMinus: () => void;
  onPlus: () => void;
  deltaDown: number | null;
  deltaUp: number | null;
  isMinusDisabled: boolean;
  isPlusDisabled: boolean;
  minusA11yLabel: string;
  plusA11yLabel: string;
  minusIconColor: string;
  plusIconColor: string;
  styles: CorrectScorePickerStyles;
}

function ScorePickerColumn({
  score,
  onMinus,
  onPlus,
  deltaDown,
  deltaUp,
  isMinusDisabled,
  isPlusDisabled,
  minusA11yLabel,
  plusA11yLabel,
  minusIconColor,
  plusIconColor,
  styles,
}: ScorePickerColumnProps) {
  return (
    <View style={styles.pickerColumn}>
      <View style={styles.scoreControlRow}>
        <View style={styles.scoreControlDeltasRow}>
          <View style={styles.deltaCell}>
            <Text style={[styles.deltaText, styles[getDeltaStyleKeyVisual(deltaDown, isMinusDisabled)]]}>
              {isMinusDisabled ? '' : deltaDown != null ? formatDeltaLabelFixed(deltaDown) : '–'}
            </Text>
          </View>
          <View style={styles.deltaSpacer} />
          <View style={styles.deltaCell}>
            <Text style={[styles.deltaText, styles[getDeltaStyleKeyVisual(deltaUp, isPlusDisabled)]]}>
              {isPlusDisabled ? '' : deltaUp != null ? formatDeltaLabelFixed(deltaUp) : '–'}
            </Text>
          </View>
        </View>
        <View style={styles.scoreControlButtonsRow}>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.scoreButton,
              styles.scoreButtonMinus,
              isMinusDisabled && styles.scoreButtonDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              !isMinusDisabled && pressed && styles.scoreButtonPressed,
              !isMinusDisabled && hovered && Platform.OS === 'web' && styles.scoreButtonHover,
            ]}
            onPress={onMinus}
            disabled={isMinusDisabled}
            accessibilityRole="button"
            accessibilityLabel={minusA11yLabel}
          >
            <Minus size={18} color={minusIconColor} />
          </Pressable>
          <View style={styles.scoreDisplay}>
            <Text style={styles.scoreDisplayText}>{formatScoreDisplay(score)}</Text>
          </View>
          <Pressable
            style={({ pressed, hovered }) => [
              styles.scoreButton,
              styles.scoreButtonPlus,
              isPlusDisabled && styles.scoreButtonDisabled,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              !isPlusDisabled && pressed && styles.scoreButtonPressed,
              !isPlusDisabled && hovered && Platform.OS === 'web' && styles.scoreButtonHover,
            ]}
            onPress={onPlus}
            disabled={isPlusDisabled}
            accessibilityRole="button"
            accessibilityLabel={plusA11yLabel}
          >
            <Plus size={18} color={plusIconColor} />
          </Pressable>
        </View>
      </View>
    </View>
  );
}

export const CorrectScorePicker: React.FC<CorrectScorePickerProps> = ({
  title,
  homeName,
  awayName,
  market,
  gameId,
  handleAddOtherPick,
  isOtherPickSelected,
  initialScoreValue,
  onDelete,
  deleteButtonTestID,
  inline = false,
}) => {
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const styles = useMemo(() => getCorrectScorePickerStyles(theme, isDark), [theme, isDark]);
  const parsedInitial = useMemo(
    () => (initialScoreValue ? parseScoreKey(initialScoreValue) : null),
    [initialScoreValue]
  );
  const [homeScore, setHomeScore] = React.useState<ScorePickerValue>(
    parsedInitial?.home ?? 0
  );
  const [awayScore, setAwayScore] = React.useState<ScorePickerValue>(
    parsedInitial?.away ?? 0
  );

  const values = useMemo(() => market.values ?? [], [market.values]);
  const lookup = useMemo(() => buildScoreLookup(values), [values]);
  const bounds = useMemo(() => getCorrectScoreBounds(values), [values]);
  const { maxHome, maxAway } = bounds;

  React.useEffect(() => {
    if (initialScoreValue && parsedInitial) {
      // When bounds are (0,0) market.values may not be loaded yet; show initial score from slip instead of clamping to 0-0
      const boundsLoaded = maxHome > 0 || maxAway > 0;
      const h = boundsLoaded
        ? Math.min(Math.max(parsedInitial.home, 0), maxHome)
        : Math.max(parsedInitial.home, 0);
      const a = boundsLoaded
        ? Math.min(Math.max(parsedInitial.away, 0), maxAway)
        : Math.max(parsedInitial.away, 0);
      setHomeScore(h);
      setAwayScore(a);
      return;
    }
    setHomeScore((s) => (s > maxHome ? maxHome : s));
    setAwayScore((s) => (s > maxAway ? maxAway : s));
  }, [initialScoreValue, parsedInitial, maxHome, maxAway]);

  const currentOdd = lookup.get(getScoreKey(homeScore, awayScore)) ?? getOddForScore(homeScore, awayScore, values);

  const neighborHomeUp = getNeighborInfo('homeUp', homeScore, awayScore, lookup);
  const neighborHomeDown = getNeighborInfo('homeDown', homeScore, awayScore, lookup);
  const neighborAwayUp = getNeighborInfo('awayUp', homeScore, awayScore, lookup);
  const neighborAwayDown = getNeighborInfo('awayDown', homeScore, awayScore, lookup);

  const deltaHomeUp = neighborHomeUp.delta;
  const deltaHomeDown = neighborHomeDown.delta;
  const deltaAwayUp = neighborAwayUp.delta;
  const deltaAwayDown = neighborAwayDown.delta;

  const selectScore = useCallback(
    (newHome: ScorePickerValue, newAway: ScorePickerValue) => {
      const value = getScoreKey(newHome, newAway);
      const odd = getOddForScore(newHome, newAway, values);
      if (odd != null) {
        handleAddOtherPick?.(gameId, market.id, market.name, value, odd, homeName, awayName);
      }
    },
    [gameId, market.id, market.name, homeName, awayName, handleAddOtherPick, values]
  );

  const onHomeMinus = useCallback(() => {
    if (!neighborHomeDown.exists) return;
    const newHome = (homeScore - 1) as ScorePickerValue;
    setHomeScore(newHome);
    selectScore(newHome, awayScore);
  }, [homeScore, awayScore, neighborHomeDown.exists, selectScore]);
  const onHomePlus = useCallback(() => {
    if (!neighborHomeUp.exists) return;
    const newHome = homeScore + 1;
    setHomeScore(newHome);
    selectScore(newHome, awayScore);
  }, [homeScore, awayScore, neighborHomeUp.exists, selectScore]);
  const onAwayMinus = useCallback(() => {
    if (!neighborAwayDown.exists) return;
    const newAway = awayScore - 1;
    setAwayScore(newAway);
    selectScore(homeScore, newAway);
  }, [homeScore, awayScore, neighborAwayDown.exists, selectScore]);
  const onAwayPlus = useCallback(() => {
    if (!neighborAwayUp.exists) return;
    const newAway = awayScore + 1;
    setAwayScore(newAway);
    selectScore(homeScore, newAway);
  }, [homeScore, awayScore, neighborAwayUp.exists, selectScore]);

  const isHomeMinusDisabled = homeScore === 0 || !neighborHomeDown.exists;
  const isHomePlusDisabled = !neighborHomeUp.exists;
  const isAwayMinusDisabled = awayScore === 0 || !neighborAwayDown.exists;
  const isAwayPlusDisabled = !neighborAwayUp.exists;

  const scoreValue = getScoreKey(homeScore, awayScore);
  const selected = Boolean(isOtherPickSelected?.(gameId, market.id, scoreValue));

  const onOddPress = useCallback(() => {
    if (currentOdd == null) return;
    handleAddOtherPick?.(gameId, market.id, market.name, scoreValue, currentOdd, homeName, awayName);
  }, [currentOdd, gameId, market.id, market.name, scoreValue, homeName, awayName, handleAddOtherPick]);

  const inner = (
    <>
      {!inline && (
        <View style={styles.sectionTitleRow}>
          <Text style={styles.sectionTitle}>{title}</Text>
          {MARKET_BET_DESCRIPTIONS[market.id] ? (
            <PickInfoIcon description={MARKET_BET_DESCRIPTIONS[market.id]!} />
          ) : null}
          {onDelete ? (
            <Pressable
              style={({ pressed, hovered }) => [
                styles.sectionCardDeleteButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.sectionCardDeleteButtonPressed,
                hovered && Platform.OS === 'web' && styles.sectionCardDeleteButtonHover,
              ]}
              onPress={onDelete}
              accessibilityRole="button"
              accessibilityLabel="Remove this pick"
              testID={deleteButtonTestID}
            >
              <Trash2 size={20} color={theme.gray[600]} />
            </Pressable>
          ) : null}
        </View>
      )}
      <View style={styles.teamsRow}>
        <Text style={styles.teamLabel} numberOfLines={1}>
          {homeName}
        </Text>
        <Text style={styles.teamLabel} numberOfLines={1}>
          {awayName}
        </Text>
      </View>
      <View style={styles.pickersRow}>
        <ScorePickerColumn
          score={homeScore}
          onMinus={onHomeMinus}
          onPlus={onHomePlus}
          deltaDown={deltaHomeDown}
          deltaUp={deltaHomeUp}
          isMinusDisabled={isHomeMinusDisabled}
          isPlusDisabled={isHomePlusDisabled}
          minusA11yLabel="Decrease home score"
          plusA11yLabel="Increase home score"
          minusIconColor={theme.gray[700]}
          plusIconColor={theme.white}
          styles={styles}
        />
        <ScorePickerColumn
          score={awayScore}
          onMinus={onAwayMinus}
          onPlus={onAwayPlus}
          deltaDown={deltaAwayDown}
          deltaUp={deltaAwayUp}
          isMinusDisabled={isAwayMinusDisabled}
          isPlusDisabled={isAwayPlusDisabled}
          minusA11yLabel="Decrease away score"
          plusA11yLabel="Increase away score"
          minusIconColor={theme.gray[700]}
          plusIconColor={theme.white}
          styles={styles}
        />
      </View>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.oddsBoxBelow,
          selected && styles.oddsBoxBelowSelected,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.oddsBoxPressed,
          hovered && Platform.OS === 'web' && styles.oddsBoxHover,
        ]}
        onPress={onOddPress}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={`Correct score ${scoreValue} ${currentOdd != null ? formatOddDisplay(currentOdd) : ''}`}
      >
        {currentOdd != null ? (
          <Text style={styles.oddsBoxValue} numberOfLines={1}>
            {formatOddDisplay(currentOdd)}
          </Text>
        ) : (
          <Text style={styles.oddsBoxEmpty}>–</Text>
        )}
      </Pressable>
    </>
  );

  if (inline) return inner;
  return <View style={styles.sectionCard}>{inner}</View>;
};
